import React, { useEffect, useState } from 'react';
import styled from 'App/styled';
import ArrowIcon from 'components/Icons/Arrow';
import range from 'lodash-es/range';
import Page from './PageCount';
import PageNumber from './PageNumber';

interface StyleProps {
  disable: boolean;
}

const Wrapper = styled.nav`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 20px 0;
  position: relative;
`;

const PaginationItem = styled.li`
  display: flex;
  align-items: center;
`;

// Adjusted font-weight from 700 to 600 Replace with new token
const BorderPill = styled.a`
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 10px;
  line-height: 16px;
  text-decoration: none;
  border-radius: 50%;
  box-shadow: 0 2px 10px 0 rgb(0 0 0 / 10%);
  background-color: #ffffff;
  color: ${(props) => props.theme.brandPrimary};
  cursor: pointer;
  ${({ theme }) => theme.typography.titleXSmall};
`;

const RightIcon = styled(BorderPill)`
  pointer-events: ${({ disable }: StyleProps) => (disable ? 'none' : 'auto')};
  .arrow-icon {
    fill: ${(props) => props.theme.brandPrimary};
    transform: rotate(270deg);
  }
`;
const LeftIcon = styled(BorderPill)`
  pointer-events: ${({ disable }: StyleProps) => (disable ? 'none' : 'auto')};
  .arrow-icon {
    fill: ${(props) => props.theme.brandPrimary};
    transform: rotate(90deg);
  }
`;

const PaginationList = styled.ul`
  display: flex;
  justify-content: center;
  align-items: center;
  list-style: none;
  padding: ${({ theme }) => theme.spacing.spacing0};
  margin: ${({ theme }) => theme.spacing.spacing0};
`;

interface PaginationProps {
  startNumber: number;
  count: number;
  changePage: (page: number) => void;
  selectedPage: number;
  totalPages: number;
}

const PaginationByNumber = ({
  startNumber,
  count,
  changePage,
  selectedPage = 1,
  totalPages,
}: PaginationProps) => {
  const [startIndex, setStartIndex] = useState(startNumber);
  const disableIncrease = totalPages === Number(selectedPage);
  const disableDecrease = Number(selectedPage) === 1;
  useEffect(() => {
    if (selectedPage > 3) {
      setStartIndex(selectedPage - 2);
    } else {
      setStartIndex(1);
    }
  }, [selectedPage]);
  const onIncrease = () => {
    if (disableIncrease) {
      return null;
    }
    changePage(Number(selectedPage) + 1);
  };
  const onDecrease = () => {
    if (disableDecrease) {
      return null;
    }
    changePage(selectedPage - 1);
  };
  const handlePage = () => {
    changePage(1);
    setStartIndex(1);
  };
  const showFirstPage = startIndex !== 1 || count === 0;
  return (
    <Wrapper
      role="navigation"
      aria-label="Pagination"
    >
      <Page
        currentPage={selectedPage}
        totalPage={totalPages}
      />
      <PaginationList>
        {showFirstPage && (
          <PaginationItem>
            <BorderPill
              onClick={handlePage}
              aria-label="Page 1"
            >
              1
            </BorderPill>
          </PaginationItem>
        )}
        <PaginationItem>
          <LeftIcon
            disable={disableDecrease}
            onClick={onDecrease}
            aria-label="Previous page"
          >
            <ArrowIcon />
          </LeftIcon>
        </PaginationItem>
        {range(count).map((_, idx) => (
          <PageNumber
            idx={idx}
            key={idx + 'page'}
            totalPages={totalPages}
            number={startIndex + idx}
            selectedPage={selectedPage}
            changePage={changePage}
          />
        ))}
        <PaginationItem>
          <RightIcon
            disable={disableIncrease}
            onClick={onIncrease}
            aria-label="Next page"
          >
            <ArrowIcon />
          </RightIcon>
        </PaginationItem>
      </PaginationList>
    </Wrapper>
  );
};

export default PaginationByNumber;
